import { useQuery } from "@tanstack/react-query";
import { adminMenuApi } from "../helpers/dck_helper";
import {
  SYSTEM_ALIASES,
  flattenMenuSystems,
  ADMIN_MENU_QUERY_KEY,
  ADMIN_MENU_STALE_TIME,
} from "../helpers/menuContract";
import { useSessionStore } from "../stores/sessionStore";

/**
 * 後台左選單(ADR-0003):GetMenu 依角色回可見模組。
 *
 * 回傳 systems:
 *   - Set<system_name> → 後端已回,側欄依集合過濾(沒回的不顯示)
 *   - null            → 載入中/取失敗 → 降級為靜態全顯示
 * 純前端頁(開發工具、選購包入口)不經此集合,由側欄靜態附加。
 */
// 對照表與走訪已抽到 `helpers/menuContract.js`(RBAC 也要用,那邊不能帶 react-query)。
// 這裡 re-export 讓既有引用(smoke 的副本同步斷言等)不用改。
export { SYSTEM_ALIASES } from "../helpers/menuContract";

// queryKey 與 staleTime 本體搬到 helpers/menuContract(sessionStore 也要用,
// 這裡直接 import 它會成循環相依);re-export 讓既有引用不用改。
export { ADMIN_MENU_QUERY_KEY, ADMIN_MENU_STALE_TIME } from "../helpers/menuContract";

export function useAdminMenu() {
  // 未登入不抓選單(課題 20260831-01「首次登入選單沒全部展出」):
  // 真後端 GetMenu 不帶 token 也回 200,但內容是**匿名版**(8/31 實測只有 5 個模組,
  // 缺看板/人員/機房/資產)。未登入誤抓的那份會以 staleTime 留在快取,登入後
  // fetchQuery 判定「還新鮮」直接沿用 —— 首次登入/無痕就變成「選單缺一大半
  // +整站沒權限」,F5 才恢復。閘在這裡讓匿名請求根本不會發生;
  // login()/logout() 另清快取當第二道防線(sessionStore)。
  const isAuthenticated = useSessionStore((s) => s.isAuthenticated);
  const q = useQuery({
    queryKey: ADMIN_MENU_QUERY_KEY,
    queryFn: () => adminMenuApi.getMenu(),
    staleTime: ADMIN_MENU_STALE_TIME,
    enabled: isAuthenticated,
  });

  const systems = Array.isArray(q.data) ? flattenMenuSystems(q.data) : null;

  // 「安靜降級要吵」(ADR-0004):選單有回但 system_name 全對不上前端鍵時,
  // 側欄會無聲地過濾成幾乎全空(8/12 事故) —— 警告指路對照表
  if (systems && q.data.length > 0) {
    const knownKeys = new Set(Object.values(SYSTEM_ALIASES).concat("Dashboard"));
    const matched = [...systems].some((s) => knownKeys.has(s));
    if (!matched && !useAdminMenu.__warned) {
      useAdminMenu.__warned = true;
      console.warn(
        "[menu] GetMenu 的 system_name 全數不在 SYSTEM_ALIASES 對照表,側欄將大量隱藏:",
        q.data.map((m) => m.system_name).join(", ")
      );
    }
  }
  return { ...q, systems };
}
